import { useContext } from "react";
import { Grid2 as Grid, Stack, TextField, Typography, Box } from "@mui/material";
import { AppContext, ContactInfoErrors } from "../App";
import { matchIsValidTel, MuiTelInput } from "mui-tel-input";

export function ContactInfo() {
  const { form, updateForm, contactInfoErrors, setContactInfoErrors } = useContext(AppContext);

  const handleChange = (key: keyof ContactInfoErrors) => (event: React.ChangeEvent<HTMLInputElement>) => {
    updateForm({ [key]: event.target.value });
    // Clear error when user starts typing
    if (contactInfoErrors[key]) {
      setContactInfoErrors({ ...contactInfoErrors, [key]: undefined });
    }
  };

  const handlePhoneChange = (value: string) => {
    updateForm({ phone: value });

    // Only validate and show error if there's a value
    if (value) {
      const isValidPhone = matchIsValidTel(value);
      setContactInfoErrors((prev) => ({
        ...prev,
        phone: isValidPhone ? undefined : "Please enter a valid phone number",
      }));
    } else {
      // Clear error if field is empty (since it's optional)
      setContactInfoErrors((prev) => ({
        ...prev,
        phone: undefined,
      }));
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "900px",
        margin: "0 auto",
        padding: { xs: "1rem", sm: "2rem" },
      }}
    >
      <Stack direction="column" spacing={2} alignItems="center">
        <Typography
          variant="h6"
          textAlign="center"
          sx={{
            fontSize: { xs: "1.1rem", sm: "1.25rem" },
          }}
        >
          Please provide your contact information:
        </Typography>

        <Typography
          variant="subtitle1"
          textAlign="center"
          sx={{
            fontSize: { xs: "0.9rem", sm: "1rem" },
          }}
        >
          Accurate contact info is important to facilitate a provider match.
        </Typography>

        <Stack direction="column" spacing={2} sx={{ width: "100%", maxWidth: "500px" }}>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                onChange={handleChange("firstName")}
                placeholder="First Name"
                value={form.firstName}
                error={!!contactInfoErrors.firstName}
                helperText={contactInfoErrors.firstName}
                required
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                onChange={handleChange("lastName")}
                placeholder="Last Name"
                value={form.lastName}
                error={!!contactInfoErrors.lastName}
                helperText={contactInfoErrors.lastName}
                required
                fullWidth
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <MuiTelInput
                onChange={handlePhoneChange}
                placeholder="Phone Number (optional)"
                label="Optional"
                preferredCountries={["US", "CA"]}
                error={!!contactInfoErrors.phone}
                helperText={contactInfoErrors.phone}
                value={form.phone}
                fullWidth
                defaultCountry="US"
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                type="email"
                onChange={handleChange("email")}
                placeholder="Email"
                value={form.email}
                error={!!contactInfoErrors.email}
                helperText={contactInfoErrors.email}
                required
                fullWidth
              />
            </Grid>
          </Grid>
        </Stack>
      </Stack>
    </Box>
  );
}
